import { create } from 'zustand';

import type { ResolvedTheme, ThemeSource } from '@/config/theme/theme';

interface ThemeState {
  setTheme: (theme: ResolvedTheme, source: ThemeSource) => void;
  source: ThemeSource;
  theme: ResolvedTheme;
}

/**
 * Applies the resolved theme to the document. The single choke point for the
 * DOM side effects: Tailwind's `dark` variant keys off the `dark` class on
 * <html>, and `color-scheme` keeps native scrollbars/form controls in sync
 * (and stops webviews from auto-darkening).
 */
function applyThemeToDocument(theme: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
}

/**
 * Derived theme state — never persisted. The source of truth is the Outlook
 * host theme (source 'office') or, until/unless that is known, the OS
 * preference (source 'system'). Once an 'office' value lands it wins over
 * later system-preference changes.
 *
 * Initial state reads the existing `dark` class so the store agrees with the
 * pre-paint FOUC snippet in taskpane.html.
 */
export const useThemeStore = create<ThemeState>()((set) => ({
  setTheme: (theme, source) => {
    applyThemeToDocument(theme);
    set({ source, theme });
  },
  source: 'system',
  theme:
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light',
}));
